import React from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { InstagramLogo, YoutubeLogo, List, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
    { to: "/", label: "Home", end: true },
    { to: "/category/news", label: "News" },
    { to: "/category/reviews", label: "Reviews" },
    { to: "/category/features", label: "Features" },
    { to: "/category/videos", label: "Videos" },
];

export default function Header() {
    const { user } = useAuth();
    const [open, setOpen] = React.useState(false);
    const [scrolled, setScrolled] = React.useState(false);

    React.useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(() => {
                setScrolled(window.scrollY > 40);
                ticking = false;
            });
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`sticky top-0 z-50 border-b border-zinc-800 transition-colors duration-300 ${
                scrolled ? "bg-[#0A0A0A]/95 supports-[backdrop-filter]:bg-[#0A0A0A]/80 supports-[backdrop-filter]:backdrop-blur-md" : "bg-[#0A0A0A]"
            }`}
            data-testid="site-header"
        >
            <div className={`max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between transition-[height] duration-300 ${scrolled ? "h-14" : "h-20"}`}>
                <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                    <span className={`inline-block bg-[#FFD000] grid place-items-center font-display text-black transition-all duration-300 ${scrolled ? "w-7 h-7 text-base" : "w-9 h-9 text-xl"}`}>T</span>
                    <span className="font-display text-2xl tracking-wide text-white group-hover:text-[#FFD000] transition-colors">TIZONA<span className="text-[#FFD000]">.</span>TECH</span>
                </Link>

                <nav className="hidden md:flex items-center gap-10 text-[13px] font-bold uppercase tracking-[0.18em] text-zinc-300" data-testid="main-nav">
                    {navItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            data-testid={`nav-${it.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `nav-link transition-colors hover:text-[#FFD000] ${isActive ? "text-[#FFD000] nav-link-active" : ""}`
                            }
                        >
                            {it.label}
                        </NavLink>
                    ))}
                    {/* Tizona Trends link masked per user request — see git blame to restore */}
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <a href="https://www.youtube.com/channel/UCK7IwwD0gvvompUwdg09GmA" target="_blank" rel="noreferrer" data-testid="youtube-link"
                       className="text-zinc-400 hover:text-[#FFD000] transition-colors">
                        <YoutubeLogo size={22} weight="bold" />
                    </a>
                    <a href="https://www.instagram.com/tizonatech" target="_blank" rel="noreferrer" data-testid="instagram-link"
                       className="text-zinc-400 hover:text-[#FFD000] transition-colors">
                        <InstagramLogo size={22} weight="bold" />
                    </a>
                    {user?.is_admin && (
                        <>
                            <span className="w-px h-6 bg-zinc-800" />
                            <Link to="/admin" data-testid="admin-link" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFD000] hover:text-white transition-colors">
                                Admin
                            </Link>
                        </>
                    )}
                </div>

                <button onClick={() => setOpen(!open)} className="md:hidden text-white" data-testid="mobile-menu-toggle">
                    {open ? <X size={26} /> : <List size={26} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="md:hidden border-t border-zinc-800 bg-[#0A0A0A] overflow-hidden"
                        data-testid="mobile-nav"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="px-6 py-6 flex flex-col gap-4 text-sm font-bold uppercase tracking-[0.18em]">
                            {navItems.map((it) => (
                                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
                                    className="text-zinc-300 hover:text-[#FFD000]">
                                    {it.label}
                                </NavLink>
                            ))}
                            {/* Tizona Trends mobile link masked per user request */}
                            <div className="flex items-center gap-5 pt-2">
                                <a href="https://www.youtube.com/channel/UCK7IwwD0gvvompUwdg09GmA" target="_blank" rel="noreferrer" className="text-zinc-400">
                                    <YoutubeLogo size={22} weight="bold" />
                                </a>
                                <a href="https://www.instagram.com/tizonatech" target="_blank" rel="noreferrer" className="text-zinc-400">
                                    <InstagramLogo size={22} weight="bold" />
                                </a>
                                {user?.is_admin && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="text-[#FFD000] font-mono text-xs">Admin</Link>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
